<template>
  <page-bar title="Recipes">
    <v-menu>
      <template v-slot:activator="{ props: menuProps }">
        <v-btn
          variant="text"
          v-bind="menuProps"
          prepend-icon="mdi-import"
          append-icon="mdi-chevron-down"
        >
          Import
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          prepend-icon="mdi-camera"
          title="From photo"
          @click="showImageImport = true"
        />
        <v-list-item
          prepend-icon="mdi-link"
          title="From URL"
          @click="showUrlImport = true"
        />
      </v-list>
    </v-menu>
  </page-bar>
  <v-container class="recipes">
    <!-- The screen's main action, in thumb reach: a Material 3 extended FAB -->
    <v-fab
      extended
      prepend-icon="mdi-plus"
      text="Add recipe"
      color="primary-container"
      class="add-recipe"
      @click="openDialog()"
    />
    <v-row>
      <import-recipe-from-image
        :client="client"
        :open="showImageImport"
        @imported="onImported"
        @close="showImageImport = false"
      />
      <import-recipe-from-url
        :client="client"
        :open="showUrlImport"
        @imported="onImported"
        @close="showUrlImport = false"
      />
    </v-row>
    <v-row>
      <v-col cols="12">
        <recipe-filters v-model="filter" :tags="tags" />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        :cols="cols($vuetify.display.name)"
        v-for="recipe in shown"
        :key="recipe.id"
      >
        <recipe-card
          :recipe="recipe"
          :favorite="favorites.includes(recipe.id)"
          @toggle-favorite="toggleFavorite(recipe.id)"
        />
      </v-col>
      <v-col v-if="!shown.length">
        <v-card elevation="0">
          <v-card-title class="text-center">
            <v-icon icon="mdi-emoticon-sad-outline" size="100"></v-icon>
            <p>
              {{ recipes.length ? 'No recipes match' : 'No recipes found!' }}
            </p>
          </v-card-title>
          <v-card-actions v-if="recipes.length" class="justify-center">
            <v-btn variant="tonal" @click="clearFilter">Clear filters</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <recipe-dialog
      :tag-suggestions="tags"
      :loading="loading"
      :open="dialog"
      :recipe="importedRecipe"
      @submit="submit"
      @close="closeDialog"
    />
  </v-container>
</template>

<script lang="ts">
  import { computed, defineComponent, type PropType, ref } from 'vue';
  import RecipeCard from '@/components/recipe/recipe-card/RecipeCard.vue';
  import PageBar from '@/components/infrastructure/page-bar/PageBar.vue';
  import { useRecipeStore } from '@/stores/recipe';
  import RecipeDialog from '@/components/recipe/recipe-dialog/RecipeDialog.vue';
  import ImportRecipeFromUrl from '@/components/recipe/import-recipe-from-url/ImportRecipeFromUrl.vue';
  import ImportRecipeFromImage from '@/components/recipe/import-recipe-from-image/ImportRecipeFromImage.vue';
  import { cols } from '@/service/vuetifyHelper';
  import type { Recipe, RecipePayload } from '@/service/recipe/types';
  import type { ClientInterface } from '@/service/types';
  import RecipeFilters from '@/components/recipe/recipe-filters/RecipeFilters.vue';
  import {
    allTags,
    findRecipes,
    type RecipeFilter,
  } from '@/service/recipe/findRecipes';
  import type { FavoritesClientInterface } from '@/service/favorites/favoritesClient';
  import { useSettingStore } from '@/stores/settings';

  export default defineComponent({
    name: 'RecipeView',
    components: {
      PageBar,
      RecipeDialog,
      RecipeCard,
      RecipeFilters,
      ImportRecipeFromUrl,
      ImportRecipeFromImage,
    },
    props: {
      client: {
        type: Object as PropType<ClientInterface<Recipe, RecipePayload>>,
        required: true,
      },
      favoritesClient: {
        type: Object as PropType<FavoritesClientInterface>,
        required: true,
      },
    },
    setup(props) {
      const dialog = ref(false);
      const loading = ref(false);
      const importedRecipe = ref<Recipe | undefined>(undefined);
      const showImageImport = ref(false);
      const showUrlImport = ref(false);
      const store = useRecipeStore(props.client);
      const recipes = store.getRecipes();
      const favorites = props.favoritesClient.favoritesRef();
      const tags = computed(() => allTags(recipes.value));

      // Sort, tags and favourites-only are remembered; the search is not
      const settings = useSettingStore();
      const search = ref('');
      const filter = computed<RecipeFilter>({
        get: () => ({
          search: search.value,
          // A remembered tag no longer in use would hide every recipe
          tags: settings.recipeTags.filter((tag) => tags.value.includes(tag)),
          favoritesOnly: settings.favoritesOnly,
          sort: settings.recipeSort,
        }),
        set: (value) => {
          search.value = value.search;
          settings.recipeTags = value.tags;
          settings.favoritesOnly = value.favoritesOnly;
          settings.recipeSort = value.sort;
        },
      });

      return {
        recipes,
        favorites,
        tags,
        filter,
        shown: computed(() =>
          findRecipes(recipes.value, filter.value, favorites.value),
        ),
        clearFilter() {
          filter.value = {
            ...filter.value,
            search: '',
            tags: [],
            favoritesOnly: false,
          };
        },
        async toggleFavorite(id: string) {
          await props.favoritesClient.setFavorite(
            id,
            !favorites.value.includes(id),
          );
        },
        dialog,
        loading,
        importedRecipe,
        showImageImport,
        showUrlImport,
        cols,
        openDialog(recipe?: Recipe) {
          importedRecipe.value = recipe;
          dialog.value = true;
        },
        closeDialog() {
          importedRecipe.value = undefined;
          dialog.value = false;
        },
        onImported(recipe: Recipe) {
          importedRecipe.value = recipe;
          dialog.value = true;
        },
        async submit(recipe: RecipePayload) {
          loading.value = true;
          await store.save(recipe);
          loading.value = false;
          dialog.value = false;
          importedRecipe.value = undefined;
        },
      };
    },
  });
</script>

<style scoped>
  /* Room below the last card for the floating button */
  .recipes {
    padding-bottom: 96px;
  }

  .add-recipe {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 4;
  }

  /* Above the bottom navigation bar on compact windows */
  @media (max-width: 599.98px) {
    .add-recipe {
      bottom: calc(80px + 16px);
    }
  }
</style>
